import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { escapeHtml } from '../../sections/_shared/settingValue.js';
import { resolveColor } from '../../theme/colorTokens.js';
import { ALIGN_OPTIONS, marginFields, SCHEME_COLOR_OPTIONS, visibilityField, weightField, } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, schemeColor, str, visibilityCss, } from '../_shared/blockRender.js';
import { inlineMessageHtml } from '../_shared/inlineMessage.js';
/**
 * Shrine `urgency-text` ("SALE ENDS SOON | LOW STOCK") — the OUTLINED variant of the scarcity line.
 *
 * Shrine's product page carries this as a `<p class="urgency-text color-accent-1"><strong>…</strong></p>`
 * boxed in a thin 1px border of the same colour, square corners, small bold uppercase text with a wide
 * tracking, sitting ABOVE the title. It is a different object from our `urgency` block (flame icon, pipe
 * rendered as a vertical rule, `{n}` stock substitution): this one is a plain framed label whose text the
 * seller writes verbatim — the pipe stays a pipe, no icon, no pulse.
 *
 * Settings (the seller's authoring surface):
 *  - `text`          the label, verbatim. Defaults to the store-language scarcity banner.
 *  - `colorScheme`   one scheme colour drives BOTH the text and the frame (`currentColor`), so the box
 *                    always reads as one object, as in Shrine.
 *  - `alignment`     where the box sits in the column (it is inline-sized, never full width).
 *  - `textSize` / `weight` / `letterSpacing` / `uppercase` — the type.
 *  - `borderWidth` / `cornerRadius` / `paddingY` / `paddingX` — the frame. Defaults = Shrine: 1px, 0px.
 *  - `visibility` + Margins, like every product block.
 */
export const urgencyBoxBlockSchema = {
    type: 'urgency-box',
    name: 'Urgency box',
    max: 1,
    fields: [
        visibilityField,
        {
            // RICH text since 24/09/2026 (founder: « the urgency box should have the text written in a rich
            // text also ») — a word can carry its own colour. A stored plain STRING (every earlier store, and
            // what the AI composer writes) still renders, escaped. The « | » stays verbatim here, unlike the
            // urgency line: this block never drew a divider.
            id: 'text',
            type: 'richtext',
            label: 'Text',
            info: 'Shown exactly as written. Select a word and pick its colour in the toolbar. Use « | » to ' +
                'separate two mentions.',
            default: { format: 'html', html: 'PRODUIT POPULAIRE | STOCK LIMITÉ' },
            defaultKey: 'product.urgency_banner',
            // ÉCRITE PAR L'IA : même règle que la ligne d'urgence — ce qui est vrai de CE produit, jamais
            // un stock ou un compte à rebours inventé.
            aiWritable: true,
        },
        {
            id: 'colorScheme',
            aiConfigurable: true,
            type: 'select',
            label: 'Color',
            info: 'Colours the text and the frame together.',
            default: 'accent-1',
            options: SCHEME_COLOR_OPTIONS,
        },
        // ── Colors (24/09/2026: « we should be able to control the color of the box as we want and the
        // color of the text ») — three free colours over the scheme. Each is `auto` (unset = derived), so
        // a box nobody recoloured still follows its palette, and the inspector says so instead of showing
        // an empty hex.
        {
            id: 'textColor',
            type: 'color',
            label: 'Text color',
            group: 'Colors',
            auto: true,
            info: 'Automatic: the colour of the palette above.',
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border color',
            group: 'Colors',
            auto: true,
            info: 'Automatic: the same as the text.',
        },
        {
            id: 'backgroundColor',
            type: 'color',
            label: 'Background color',
            group: 'Colors',
            auto: true,
            info: 'Automatic: no background.',
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Alignment',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        // ── Text ──
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Text',
            min: 10,
            max: 20,
            step: 1,
            default: 13,
            unit: 'px',
        },
        { ...weightField, group: 'Text' },
        {
            id: 'letterSpacing',
            type: 'range',
            label: 'Letter spacing',
            group: 'Text',
            info: 'Relative to font size',
            min: 0,
            max: 20,
            step: 1,
            default: 8,
            unit: '%',
        },
        {
            id: 'uppercase',
            type: 'checkbox',
            label: 'Uppercase',
            group: 'Text',
            default: true,
        },
        // ── Frame ──
        {
            id: 'borderWidth',
            type: 'range',
            label: 'Border width',
            group: 'Frame',
            min: 0,
            max: 4,
            step: 1,
            default: 1,
            unit: 'px',
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Frame',
            min: 0,
            max: 24,
            step: 1,
            default: 0,
            unit: 'px',
        },
        {
            id: 'paddingY',
            type: 'range',
            label: 'Vertical padding',
            group: 'Frame',
            min: 0,
            max: 20,
            step: 1,
            default: 4,
            unit: 'px',
        },
        {
            id: 'paddingX',
            type: 'range',
            label: 'Horizontal padding',
            group: 'Frame',
            min: 0,
            max: 40,
            step: 1,
            default: 10,
            unit: 'px',
        },
        ...marginFields(0, 15),
    ],
};
/** left / center / right → flex `justify-content` for the box's row. */
/** A stored ColorValue → CSS, or `undefined` when the field is unset (`auto`) or not a colour. */
function customColor(v) {
    return v && typeof v === 'object' && 'ref' in v
        ? resolveColor(v)
        : undefined;
}
const JUSTIFY = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};
const UrgencyBoxBlock = ({ block, context }) => {
    const bs = block.settings;
    const cls = `aqx-ubx-${block.id}`;
    // Rich or plain, the text is inline HTML now; blank (an emptied editor, whitespace) renders nothing.
    const html = inlineMessageHtml(bs.text);
    // A text the seller EMPTIED (a blank string, an emptied editor) is a decision: nothing. Only an unset
    // field falls back to the dictionary banner.
    const emptied = bs.text !== undefined && html === '';
    const text = html || (emptied ? '' : escapeHtml(t(context, 'product.urgency_banner')));
    if (!text)
        return null;
    // Free colours win over the scheme; unset (`auto`) keeps the earlier behaviour exactly: text from the
    // palette, border = text (`currentColor`), no background.
    const color = customColor(bs.textColor) ?? schemeColor(str(bs.colorScheme, 'accent-1'));
    const borderColor = customColor(bs.borderColor) ?? 'currentColor';
    const background = customColor(bs.backgroundColor) ?? 'transparent';
    const justify = JUSTIFY[str(bs.alignment, 'left')] ?? 'flex-start';
    const size = num(bs.textSize, 13);
    // Shrine wraps the label in <strong>: Bold is the floor when the shared weight select is untouched.
    const weight = readWeight(bs) || '700';
    const tracking = num(bs.letterSpacing, 8) / 100;
    const uppercase = bs.uppercase !== false;
    const border = num(bs.borderWidth, 1);
    const radius = num(bs.cornerRadius, 0);
    const py = num(bs.paddingY, 4);
    const px = num(bs.paddingX, 10);
    // One scoped rule set: the row positions the box, the box carries the type + frame. `currentColor`
    // on the border keeps text and frame in lockstep with the single colour setting.
    const css = `.${cls}{display:flex;justify-content:${justify}}` +
        `.${cls} .aqx-ubx-in{display:inline-block;max-width:100%;box-sizing:border-box;` +
        `color:${color};background:${background};border:${border}px solid ${borderColor};` +
        `border-radius:${radius}px;` +
        `padding:${py}px ${px}px;font-size:${size}px;font-weight:${weight};` +
        `letter-spacing:${tracking}em;line-height:1.3;text-align:center;` +
        `text-transform:${uppercase ? 'uppercase' : 'none'};overflow-wrap:anywhere}`;
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css + visibilityCss(cls, bs.visibility) } }), _jsx("div", { className: cls, style: marginStyle(bs), children: _jsx("p", { className: "aqx-ubx-in", role: "status", "aria-live": "polite", style: { margin: 0 }, dangerouslySetInnerHTML: { __html: text } }) })] }));
};
export const urgencyBoxBlock = {
    type: 'urgency-box',
    schema: urgencyBoxBlockSchema,
    Component: UrgencyBoxBlock,
    meta: { label: 'Urgency box' },
};
//# sourceMappingURL=index.js.map